import React, { useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useForm, Controller } from 'react-hook-form';
import FieldErrorText from 'components/base/field-error-text';
import { IAppVersion } from 'store/VersioningStore';
import { compareVersions } from 'utils/functions';
import { PLATFORM_ICONS, VERSION_VALIDATION_RULE } from './constants';
import { Block, FieldsContainer } from './styles';

interface IVersionBlockProps {
  title: string;
  versionData?: IAppVersion;
  updateVersion: (
    store: string,
    validVersion: string,
    outdatedVersion: string,
  ) => void;
  isLoading: boolean;
  updatingStore: string | null;
  isReadOnly?: boolean;
}

const VersionBlock: React.FC<IVersionBlockProps> = ({
  title,
  versionData,
  updateVersion,
  isLoading,
  updatingStore,
  isReadOnly = false,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isValid, touchedFields },
    reset,
    watch,
    trigger,
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      validVersion: versionData?.validVersion || '',
      outdatedVersion: versionData?.outdatedVersion || '',
    },
  });

  const outdatedVersion = watch('outdatedVersion');
  const validVersion = watch('validVersion');

  useEffect(() => {
    reset({
      validVersion: versionData?.validVersion || '',
      outdatedVersion: versionData?.outdatedVersion || '',
    });
  }, [versionData, reset]);

  // Ревалидация validVersion при изменении outdatedVersion
  useEffect(() => {
    if (touchedFields.validVersion) {
      trigger('validVersion');
    }
  }, [outdatedVersion, trigger, touchedFields.validVersion]);

  // Ревалидация outdatedVersion при изменении validVersion
  useEffect(() => {
    if (touchedFields.outdatedVersion) {
      trigger('outdatedVersion');
    }
  }, [validVersion, trigger, touchedFields.outdatedVersion]);

  const onSubmit = (data: {
    validVersion: string;
    outdatedVersion: string;
  }) => {
    updateVersion(title, data.validVersion, data.outdatedVersion);
  };

  const validateValidVersion = (value: string) => {
    if (!value || !outdatedVersion) {
      return true;
    }

    const isValidVersion =
      VERSION_VALIDATION_RULE.pattern.value.test(value) &&
      VERSION_VALIDATION_RULE.pattern.value.test(outdatedVersion);

    if (!isValidVersion) {
      return true;
    }

    return (
      compareVersions(value, outdatedVersion) > 0 ||
      'Минимальная версия должна быть больше критически устаревшей версии'
    );
  };

  const validateOutdatedVersion = (value: string) => {
    if (!value || !validVersion) {
      return true;
    }

    const isValidVersion =
      VERSION_VALIDATION_RULE.pattern.value.test(value) &&
      VERSION_VALIDATION_RULE.pattern.value.test(validVersion);

    if (!isValidVersion) {
      return true;
    }

    return (
      compareVersions(validVersion, value) > 0 ||
      'Критически устаревшая версия должна быть меньше минимальной версии'
    );
  };

  return (
    <Block>
      <div className="title-container">
        <img
          src={PLATFORM_ICONS[title]}
          alt={title}
          className="store-icon"
        />
        <h4>{title}</h4>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FieldsContainer>
          <div className="field">
            <label htmlFor={`${title}-valid`}>Минимальная версия</label>
            <Controller
              name="validVersion"
              control={control}
              rules={{
                ...VERSION_VALIDATION_RULE,
                validate: validateValidVersion,
              }}
              render={({ field, fieldState }) => (
                <InputText
                  id={`${title}-valid`}
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  value={field.value}
                  ref={field.ref}
                  className={fieldState.invalid ? 'p-invalid' : ''}
                  disabled={isReadOnly}
                />
              )}
            />
            {errors.validVersion?.message && (
              <FieldErrorText errorText={errors.validVersion.message} />
            )}
          </div>
          <div className="field">
            <label htmlFor={`${title}-outdated`}>
              Критически устаревшая версия
            </label>
            <Controller
              name="outdatedVersion"
              control={control}
              rules={{
                ...VERSION_VALIDATION_RULE,
                validate: validateOutdatedVersion,
              }}
              render={({ field, fieldState }) => (
                <InputText
                  id={`${title}-outdated`}
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  value={field.value}
                  ref={field.ref}
                  className={fieldState.invalid ? 'p-invalid' : ''}
                  disabled={isReadOnly}
                />
              )}
            />
            {errors.outdatedVersion?.message && (
              <FieldErrorText errorText={errors.outdatedVersion.message} />
            )}
          </div>
        </FieldsContainer>
        <Button
          label="Сохранить"
          className="p-button-success"
          disabled={!isValid || isLoading || isReadOnly}
          loading={isLoading && updatingStore === title}
          type="submit"
        />
      </form>
    </Block>
  );
};

export default VersionBlock;
